import React, { ReactChild } from "react";
import { CalendarDefinition, ViewMode } from "../../types/public-types";
import { TopPartOfCalendar } from "./top-part-of-calendar";
import {
  getCachedDateTimeFormat,
  getLocalDayOfWeek,
  addToDate,
} from "../../helpers/date-helper";
import {
  getQuarterNumber,
  isOffDay,
} from "../../helpers/calendar-helper";
import { DateSetup } from "../../types/date-setup";
import styles from "./calendar.module.css";

export type CalendarProps = {
  dateSetup: DateSetup;
  locale: string;
  viewMode: ViewMode;
  rtl: boolean;
  headerHeight: number;
  columnWidth: number;
  fontFamily: string;
  fontSize: string;
  projectCalendar?: CalendarDefinition;
  weekendColor: string;
};

export const Calendar: React.FC<CalendarProps> = ({
  dateSetup,
  locale,
  viewMode,
  rtl,
  headerHeight,
  columnWidth,
  fontFamily,
  fontSize,
  projectCalendar,
  weekendColor,
}) => {
  /** Off-day test — mirrors GridBody.isDayOff so header shading matches the body. */
  const isDayOff = (date: Date): boolean => {
    if (projectCalendar) return isOffDay(date, projectCalendar);
    
    return false; // default to no off days if no calendar provided, to avoid false positives on custom view modes like Hourly
  };
  /** Helper: 3-letter month abbreviation */
  const shortMonth = (date: Date) =>
    date.toLocaleString(locale, { month: "short" });

  const getCalendarValuesForYear = () => {
    const topValues: ReactChild[] = [];
    const bottomValues: ReactChild[] = [];
    const topDefaultHeight = headerHeight * 0.5;
    for (let i = 0; i < dateSetup.dates.length; i++) {
      const date = dateSetup.dates[i];
      const bottomValue = date.getFullYear();
      bottomValues.push(
        <text
          key={date.getFullYear()}
          y={headerHeight * 0.8}
          x={columnWidth * i + columnWidth * 0.5}
          className={styles.calendarBottomText}
        >
          {bottomValue}
        </text>
      );
      if (
        i === 0 ||
        date.getFullYear() !== dateSetup.dates[i - 1].getFullYear()
      ) {
        const topValue = date.getFullYear().toString();
        let xText: number;
        if (rtl) {
          xText = (6 + i + date.getFullYear() + 1) * columnWidth;
        } else {
          xText = (6 + i - date.getFullYear()) * columnWidth;
        }
        topValues.push(
          <TopPartOfCalendar
            key={topValue}
            value={topValue}
            x1Line={columnWidth * i}
            y1Line={0}
            y2Line={headerHeight}
            xText={xText}
            yText={topDefaultHeight * 0.9}
          />
        );
      }
    }
    return [topValues, bottomValues];
  };

  /**
   * QUARTER view — 3-row header: Year (top) | Qtr label (middle) | Month abbr (bottom)
   */
  const getCalendarValuesForQuarter = () => {
    const topValues: ReactChild[] = [];
    const bottomValues: ReactChild[] = [];
    const offDayRects: ReactChild[] = [];
    const quarterStart = projectCalendar?.quarter_start ?? 0;
    const rowH = headerHeight / 3;

    for (let i = 0; i < dateSetup.dates.length; i++) {
      const date = dateSetup.dates[i];
      const qNum = getQuarterNumber(date, quarterStart);

      // Bottom row: 3 month abbreviations within each quarter column
      for (let m = 0; m < 3; m++) {
        const monthDate = addToDate(date, m, "month");
        bottomValues.push(
          <text
            key={`qmonth-${i}-${m}`}
            y={headerHeight * 0.9}
            x={columnWidth * i + columnWidth * (m * 2 + 1) / 6}
            className={`${styles.calendarTopText} ${styles.textAnchorMiddle}`}
          >
            {shortMonth(monthDate)}
          </text>
        );
      }

      // Middle row: quarter label centered on column
      bottomValues.push(
        <text
          key={`qlabel-${i}`}
          y={rowH * 1.6}
          x={columnWidth * i + columnWidth * 0.5}
          className={`${styles.calendarTopText} ${styles.textAnchorMiddle}`}
        >
          {`Qtr ${qNum}`}
        </text>
      );

      // Vertical separator at start of each column
      bottomValues.push(
        <line
          key={`qsep-${i}`}
          x1={columnWidth * i}
          y1={rowH}
          x2={columnWidth * i}
          y2={headerHeight}
          className={styles.calendarTopTick}
        />
      );

      // Horizontal separator between rows
      if (i === 0) {
        bottomValues.push(
          <line
            key="qhsep1"
            x1={0}
            y1={rowH}
            x2={columnWidth * dateSetup.dates.length}
            y2={rowH}
            className={styles.calendarTopTick}
          />
        );
        bottomValues.push(
          <line
            key="qhsep2"
            x1={0}
            y1={rowH * 2}
            x2={columnWidth * dateSetup.dates.length}
            y2={rowH * 2}
            className={styles.calendarTopTick}
          />
        );
      }

      // Top row: year label — new group when year changes
      if (
        i === 0 ||
        date.getFullYear() !== dateSetup.dates[i - 1].getFullYear()
      ) {
        const topValue = date.getFullYear().toString();
        // Find span end for xText calculation
        let span = 0;
        for (let j = i; j < dateSetup.dates.length && dateSetup.dates[j].getFullYear() === date.getFullYear(); j++) {
          span++;
        }
        const xText = columnWidth * i + (columnWidth * span) / 2;
        topValues.push(
          <TopPartOfCalendar
            key={topValue + i}
            value={topValue}
            x1Line={columnWidth * i}
            y1Line={0}
            y2Line={rowH}
            xText={xText}
            yText={rowH * 0.7}
            textAnchor="middle"
          />
        );
      }
    }

    return [topValues, bottomValues, offDayRects];
  };

  /**
   * MONTH view — top row shows Quarter labels, bottom row shows 3-letter months.
   */
  const getCalendarValuesForMonth = () => {
    const topValues: ReactChild[] = [];
    const bottomValues: ReactChild[] = [];
    const offDayRects: ReactChild[] = [];
    const topDefaultHeight = headerHeight * 0.5;
    const quarterStart = projectCalendar?.quarter_start ?? 0;

    for (let i = 0; i < dateSetup.dates.length; i++) {
      const date = dateSetup.dates[i];
      const bottomValue = shortMonth(date);
      bottomValues.push(
        <text
          key={bottomValue + date.getFullYear() + i}
          y={headerHeight * 0.8}
          x={columnWidth * i + columnWidth * 0.5}
          className={`${styles.calendarTopText} ${styles.textAnchorMiddle}`}
        >
          {bottomValue}
        </text>
      );

      // Top row: quarter label — new group when quarter or year changes
      const qNum = getQuarterNumber(date, quarterStart);
      const prevDate = i > 0 ? dateSetup.dates[i - 1] : null;
      const prevQNum = prevDate ? getQuarterNumber(prevDate, quarterStart) : -1;
      const isNewQuarter =
        i === 0 ||
        qNum !== prevQNum ||
        date.getFullYear() !== (prevDate ? prevDate.getFullYear() : -1);

      if (isNewQuarter) {
        const topValue = `Qtr ${qNum}, ${date.getFullYear()}`;
        // Find how many months in this quarter-year group for xText centering
        let span = 0;
        for (
          let j = i;
          j < dateSetup.dates.length &&
          getQuarterNumber(dateSetup.dates[j], quarterStart) === qNum &&
          dateSetup.dates[j].getFullYear() === date.getFullYear();
          j++
        ) {
          span++;
        }
        const xText = rtl
          ? columnWidth * i + columnWidth * span * 0.5 + columnWidth
          : columnWidth * i + (columnWidth * span) / 2;

        topValues.push(
          <TopPartOfCalendar
            key={topValue + i}
            value={topValue}
            x1Line={columnWidth * i}
            y1Line={0}
            y2Line={topDefaultHeight}
            xText={xText}
            yText={topDefaultHeight * 0.7}
            textAnchor="middle"
          />
        );
        // Extend the quarter-boundary tick through the bottom half of the header
        if (i > 0) {
          bottomValues.push(
            <line
              key={`qbound-${i}`}
              x1={columnWidth * i}
              y1={topDefaultHeight}
              x2={columnWidth * i}
              y2={headerHeight}
              className={styles.calendarTopTick}
            />
          );
        }
      }
    }
    return [topValues, bottomValues, offDayRects];
  };

  /**
   * WEEK view — top row: "Mmm YYYY", bottom row: day-number of week start.
   */
  const getCalendarValuesForWeek = () => {
    const topValues: ReactChild[] = [];
    const bottomValues: ReactChild[] = [];
    const offDayRects: ReactChild[] = [];
    let weeksCount: number = 1;
    const topDefaultHeight = headerHeight * 0.5;
    const dates = dateSetup.dates;
    for (let i = dates.length - 1; i >= 0; i--) {
      const date = dates[i];
      let topValue = "";
      if (i === 0 || date.getMonth() !== dates[i - 1].getMonth()) {
        // Use 3-letter month abbreviation
        topValue = `${shortMonth(date)} ${date.getFullYear()}`;
      }
      // Bottom: day number of the week-start date
      const bottomValue = date.getDate().toString();

      bottomValues.push(
        <text
          key={date.getTime()}
          y={headerHeight * 0.8}
          x={columnWidth * (i + +rtl)}
          className={`${styles.calendarTopText} ${styles.textAnchorStart}`}
        >
          {bottomValue}
        </text>
      );

      if (topValue) {
        if (i !== dates.length - 1) {
          topValues.push(
            <TopPartOfCalendar
              key={topValue + i}
              value={topValue}
              x1Line={columnWidth * i + weeksCount * columnWidth}
              y1Line={0}
              y2Line={topDefaultHeight}
              xText={columnWidth * i + columnWidth * weeksCount * 0.5}
              yText={topDefaultHeight * 0.9}
              textAnchor="start"
            />
          );
        }
        weeksCount = 0;
      }
      weeksCount++;
    }
    return [topValues, bottomValues, offDayRects];
  };

  /**
   * DAY view — top row: "Mmm YYYY" per month, bottom: day labels.
   * Off days shaded gray. Solid tick for week-start day, dashed for others.
   */
  const getCalendarValuesForDay = () => {
    const topValues: ReactChild[] = [];
    const bottomValues: ReactChild[] = [];
    const offDayRects: ReactChild[] = [];
    const tickLines: ReactChild[] = [];
    const topDefaultHeight = headerHeight * 0.5;
    const dates = dateSetup.dates;
    const weekStartDay = projectCalendar?.week_start ?? 1; // default Monday

    for (let i = 0; i < dates.length; i++) {
      const date = dates[i];

      // Off-day shading on the day-number row (bottom band of the header).
      // Drawn into extraValues, which renders before tickLines so the solid
      // week-start line stays on top of the gray. Matches the grid body shading.
      if (isDayOff(date)) {
        offDayRects.push(
          <rect
            key={`offday-header-${i}`}
            x={columnWidth * i}
            y={topDefaultHeight}
            width={columnWidth}
            height={headerHeight - topDefaultHeight}
            fill={weekendColor}
          />
        );
      }

      // Per-day separator tick lines
      const isWeekStart = date.getDay() === weekStartDay;
      tickLines.push(
        <line
          key={`tick-day-${i}`}
          x1={columnWidth * i}
          y1={topDefaultHeight}
          x2={columnWidth * i}
          y2={headerHeight}
          className={isWeekStart ? styles.calendarTickSolid : styles.calendarTickDashed}
        />
      );

      // Bottom: day label
      const bottomValue =
        columnWidth > 55
          ? `${getLocalDayOfWeek(date, locale, "short")}, ${date.getDate()}`
          : `${getLocalDayOfWeek(date, locale, "narrow")},${date.getDate()}`;
      bottomValues.push(
        <text
          key={date.getTime()}
          y={headerHeight * 0.8}
          x={columnWidth * i + columnWidth * 0.5}
          className={`${styles.calendarTopText} ${styles.textAnchorMiddle}`}
        >
          {bottomValue}
        </text>
      );

      // Top: month boundary labels using 3-letter abbreviation
      if (
        i + 1 !== dates.length &&
        date.getMonth() !== dates[i + 1].getMonth()
      ) {
        const topValue = `${shortMonth(date)} ${date.getFullYear()}`;
        topValues.push(
          <TopPartOfCalendar
            key={topValue + i}
            value={topValue}
            x1Line={columnWidth * (i + 1)}
            y1Line={0}
            y2Line={topDefaultHeight}
            xText={
              topValues.length === 0
                ? columnWidth * (i + 1) * 0.5
                : columnWidth * (i + 1) - date.getDate() * columnWidth * 0.5
            }
            yText={topDefaultHeight * 0.9}
          />
        );
      }
      if (i + 1 === dates.length) {
        const topValue = `${shortMonth(date)} ${date.getFullYear()}`;
        topValues.push(
          <TopPartOfCalendar
            key={topValue + "last"}
            value={topValue}
            x1Line={columnWidth * (i + 1)}
            y1Line={0}
            y2Line={topDefaultHeight}
            xText={columnWidth * (i + 1) - date.getDate() * columnWidth * 0.5}
            yText={topDefaultHeight * 0.9}
          />
        );
      }
    }
    return [topValues, bottomValues, offDayRects, tickLines];
  };

  const getCalendarValuesForPartOfDay = () => {
    const topValues: ReactChild[] = [];
    const bottomValues: ReactChild[] = [];
    const ticks = viewMode === ViewMode.HalfDay ? 2 : 4;
    const topDefaultHeight = headerHeight * 0.5;
    const dates = dateSetup.dates;
    for (let i = 0; i < dates.length; i++) {
      const date = dates[i];
      const bottomValue = getCachedDateTimeFormat(locale, {
        hour: "numeric",
      }).format(date);

      bottomValues.push(
        <text
          key={date.getTime()}
          y={headerHeight * 0.8}
          x={columnWidth * (i + +rtl)}
          className={`${styles.calendarTopText} ${styles.textAnchorMiddle}`}
          fontFamily={fontFamily}
        >
          {bottomValue}
        </text>
      );
      if (i === 0 || date.getDate() !== dates[i - 1].getDate()) {
        const topValue = `${getLocalDayOfWeek(date, locale, "short")}, ${date.getDate()} ${shortMonth(date)}`;
        topValues.push(
          <TopPartOfCalendar
            key={topValue + date.getFullYear()}
            value={topValue}
            x1Line={columnWidth * i + ticks * columnWidth}
            y1Line={0}
            y2Line={topDefaultHeight}
            xText={columnWidth * i + ticks * columnWidth * 0.5}
            yText={topDefaultHeight * 0.9}
          />
        );
      }
    }

    return [topValues, bottomValues];
  };

  const getCalendarValuesForHour = () => {
    const topValues: ReactChild[] = [];
    const bottomValues: ReactChild[] = [];
    const topDefaultHeight = headerHeight * 0.5;
    const dates = dateSetup.dates;
    for (let i = 0; i < dates.length; i++) {
      const date = dates[i];
      const bottomValue = getCachedDateTimeFormat(locale, {
        hour: "numeric",
      }).format(date);

      bottomValues.push(
        <text
          key={date.getTime()}
          y={headerHeight * 0.8}
          x={columnWidth * (i + +rtl)}
          className={styles.calendarBottomText}
          fontFamily={fontFamily}
        >
          {bottomValue}
        </text>
      );
      if (i !== 0 && date.getDate() !== dates[i - 1].getDate()) {
        const displayDate = dates[i - 1];
        const topValue = `${getLocalDayOfWeek(displayDate, locale, "long")}, ${displayDate.getDate()} ${shortMonth(displayDate)}`;
        const topPosition = (date.getHours() - 24) / 2;
        topValues.push(
          <TopPartOfCalendar
            key={topValue + displayDate.getFullYear()}
            value={topValue}
            x1Line={columnWidth * i}
            y1Line={0}
            y2Line={topDefaultHeight}
            xText={columnWidth * (i + topPosition)}
            yText={topDefaultHeight * 0.9}
          />
        );
      }
    }

    return [topValues, bottomValues];
  };

  let topValues: ReactChild[] = [];
  let bottomValues: ReactChild[] = [];
  let extraValues: ReactChild[] = [];
  let tickLines: ReactChild[] = [];

  switch (dateSetup.viewMode) {
    case ViewMode.Year:
      [topValues, bottomValues] = getCalendarValuesForYear();
      break;
    case ViewMode.Quarter: {
      const r = getCalendarValuesForQuarter();
      topValues = r[0]; bottomValues = r[1]; extraValues = r[2] || [];
      break;
    }
    case ViewMode.Month: {
      const r = getCalendarValuesForMonth();
      topValues = r[0]; bottomValues = r[1]; extraValues = r[2] || [];
      break;
    }
    case ViewMode.Week: {
      const r = getCalendarValuesForWeek();
      topValues = r[0]; bottomValues = r[1]; extraValues = r[2] || [];
      break;
    }
    case ViewMode.Day: {
      const result = getCalendarValuesForDay();
      topValues = result[0];
      bottomValues = result[1];
      extraValues = result[2];
      tickLines = result[3];
      break;
    }
    case ViewMode.QuarterDay:
    case ViewMode.HalfDay:
      [topValues, bottomValues] = getCalendarValuesForPartOfDay();
      break;
    case ViewMode.Hour:
      [topValues, bottomValues] = getCalendarValuesForHour();
  }
  return (
    <g
      className="calendar"
      fontSize={fontSize}
      fontFamily={fontFamily}
      width={columnWidth * dateSetup.dates.length}
    >
      <rect
        x={0}
        y={0}
        width={columnWidth * dateSetup.dates.length}
        height={headerHeight}
        className={styles.calendarHeader}
      />
      {extraValues}
      {tickLines}
      {bottomValues}
      {topValues}
    </g>
  );
};
